import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Loader2,
  Users,
  BarChart3,
  Calendar,
  Target,
  CheckCircle,
  Clock,
  AlertTriangle,
  FileText,
  ShieldAlert,
  ArrowUp,
} from 'lucide-react';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { useUserProfileContext } from '../context/UserProfileContext';
import { useApiClient } from '../hooks/useApiClient';
import { formatDateInIST } from '../utils/mappers';
import { FeedbackRequest, PipInfo } from '../types/feedback';
import { mapApiFeedbackRequestsToList } from '../utils/mappers';
import { Pagination } from '../components/common/Pagination';

const ITEMS_PER_PAGE = 10;

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface EmployeeSummary {
  employeeId: string;
  employeeName: string;
  totalConnects: number;
  completed: number;
  inProgress: number;
  revisionRequested: number;
  corePrioritySet: boolean;
  isOnPip: boolean;
  pipEndDate?: string;
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export function AnalyticsPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [empSummaryPage, setEmpSummaryPage] = useState(1);

  // Raw data
  const [teamRequests, setTeamRequests] = useState<FeedbackRequest[]>([]);
  const [corePriorityMap, setCorePriorityMap] = useState<Record<string, boolean>>({});
  const [pipMap, setPipMap] = useState<Record<string, PipInfo | null>>({});

  const { userProfile, loading: profileLoading } = useUserProfileContext();
  const { apiCall } = useApiClient();

  const hasDirectReports = useMemo(
    () => (userProfile?.directReports?.length || 0) > 0,
    [userProfile?.directReports?.length],
  );

  const directReports = useMemo(
    () => userProfile?.directReports || [],
    [userProfile?.directReports],
  );

  // Redirect non-managers
  useEffect(() => {
    if (!profileLoading && userProfile && !hasDirectReports) {
      navigate('/dashboard');
    }
  }, [profileLoading, userProfile, hasDirectReports, navigate]);

  // Scroll-to-top
  useEffect(() => {
    const onScroll = () => setShowScrollTop(window.scrollY > 300);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollToTop = useCallback(
    () => window.scrollTo({ top: 0, behavior: 'smooth' }),
    [],
  );

  // Year options
  const yearOptions = useMemo(() => {
    const cur = new Date().getFullYear();
    return Array.from({ length: 5 }, (_, i) => cur - 3 + i).reverse();
  }, []);

  /* ---- Data fetching ---- */

  useEffect(() => {
    if (!userProfile?.employeeId || !hasDirectReports) {
      if (userProfile && !hasDirectReports) setLoading(false);
      return;
    }

    const fetchAll = async () => {
      setLoading(true);

      try {
        // 1. Team connects
        const connResp = await apiCall(
          `${process.env.REACT_APP_API_BASE_URL}/api/FeedbackRequests/by-employees?ids=${encodeURIComponent(userProfile.directReports.map((r: any) => r.employeeId).join(','))}`,
        );
        let requests: FeedbackRequest[] = [];
        if (connResp.ok) {
          const res = await connResp.json();
          if (res.data) requests = mapApiFeedbackRequestsToList(res.data);
        }
        setTeamRequests(requests);

        // 2. Core priorities per direct report for selectedYear
        const cpResults: Record<string, boolean> = {};
        await Promise.all(
          directReports.map(async (dr) => {
            try {
              const resp = await apiCall(
                `${process.env.REACT_APP_API_BASE_URL}/api/CorePriorities/employee/${encodeURIComponent(dr.employeeId)}/year/${selectedYear}`,
              );
              if (resp.ok) {
                const r = await resp.json();
                cpResults[dr.employeeId] = !!(r.success && r.data);
              } else {
                cpResults[dr.employeeId] = false;
              }
            } catch {
              cpResults[dr.employeeId] = false;
            }
          }),
        );
        setCorePriorityMap(cpResults);

        // 3. Active PIP per direct report
        const pipResults: Record<string, PipInfo | null> = {};
        await Promise.all(
          directReports.map(async (dr) => {
            try {
              const resp = await apiCall(
                `${process.env.REACT_APP_API_BASE_URL}/api/pip/employee/${encodeURIComponent(dr.employeeId)}/active`,
              );
              if (resp.ok) {
                const r = await resp.json();
                pipResults[dr.employeeId] = r.success && r.data ? r.data : null;
              } else {
                pipResults[dr.employeeId] = null;
              }
            } catch {
              pipResults[dr.employeeId] = null;
            }
          }),
        );
        setPipMap(pipResults);
      } catch (err) {
        console.error('Analytics fetch error', err);
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProfile?.employeeId, hasDirectReports, selectedYear]);

  /* ---- Derived analytics ---- */

  const yearRequests = useMemo(
    () => teamRequests.filter((r) => r.year === selectedYear),
    [teamRequests, selectedYear],
  );

  const statusCounts = useMemo(() => {
    const completed = yearRequests.filter((r) => r.status === 'completed').length;
    const inProgress = yearRequests.filter((r) => r.status === 'in_progress').length;
    const revisionRequested = yearRequests.filter((r) => r.status === 'revision_requested').length;
    return { completed, inProgress, revisionRequested, total: yearRequests.length };
  }, [yearRequests]);

  const completionRate = useMemo(
    () => (statusCounts.total > 0 ? (statusCounts.completed / statusCounts.total) * 100 : 0),
    [statusCounts],
  );

  // Donut chart data
  const donutData = useMemo(() => {
    const items = [
      { name: 'Completed', value: statusCounts.completed, color: '#22c55e' },
      { name: 'In Progress', value: statusCounts.inProgress, color: '#3b82f6' },
      { name: 'Revision Requested', value: statusCounts.revisionRequested, color: '#f59e0b' },
    ].filter((d) => d.value > 0);
    return items.length > 0 ? items : [{ name: 'No Data', value: 1, color: '#e2e8f0' }];
  }, [statusCounts]);

  // Bar chart — connects per quarter
  const quarterData = useMemo(() => {
    const quarters = ['Q1', 'Q2', 'Q3', 'Q4'];
    return quarters.map((q) => {
      const qReqs = yearRequests.filter((r) => r.quarter === q);
      return {
        quarter: q,
        Completed: qReqs.filter((r) => r.status === 'completed').length,
        'In Progress': qReqs.filter((r) => r.status === 'in_progress').length,
        'Revision Requested': qReqs.filter((r) => r.status === 'revision_requested').length,
      };
    });
  }, [yearRequests]);

  // Core priorities coverage
  const cpCoverage = useMemo(() => {
    const total = directReports.length;
    const set = Object.values(corePriorityMap).filter(Boolean).length;
    return { set, total, pct: total > 0 ? (set / total) * 100 : 0 };
  }, [corePriorityMap, directReports]);

  // PIP overview
  const pipOverview = useMemo(() => {
    const active = directReports.filter((dr) => pipMap[dr.employeeId]?.isOnPip);
    return {
      count: active.length,
      employees: active.map((dr) => ({
        name: dr.displayName,
        endDate: pipMap[dr.employeeId]?.pipEndDate,
      })),
    };
  }, [pipMap, directReports]);

  // Reset employee summary page when year changes
  useEffect(() => {
    setEmpSummaryPage(1);
  }, [selectedYear]);

  // Per-employee summary table
  const employeeSummaries: EmployeeSummary[] = useMemo(() => {
    return directReports.map((dr) => {
      const empReqs = yearRequests.filter((r) => r.employeeId === dr.employeeId);
      return {
        employeeId: dr.employeeId,
        employeeName: dr.displayName,
        totalConnects: empReqs.length,
        completed: empReqs.filter((r) => r.status === 'completed').length,
        inProgress: empReqs.filter((r) => r.status === 'in_progress').length,
        revisionRequested: empReqs.filter((r) => r.status === 'revision_requested').length,
        corePrioritySet: !!corePriorityMap[dr.employeeId],
        isOnPip: !!pipMap[dr.employeeId]?.isOnPip,
        pipEndDate: pipMap[dr.employeeId]?.pipEndDate,
      };
    });
  }, [directReports, yearRequests, corePriorityMap, pipMap]);

  // Paginated employee summaries
  const empSummaryTotalPages = Math.ceil(employeeSummaries.length / ITEMS_PER_PAGE);
  const paginatedEmployeeSummaries = useMemo(() => {
    const start = (empSummaryPage - 1) * ITEMS_PER_PAGE;
    return employeeSummaries.slice(start, start + ITEMS_PER_PAGE);
  }, [employeeSummaries, empSummaryPage]);

  /* ---- Render ---- */

  if (profileLoading || loading) {
    return (
      <div className="pt-24 min-h-screen flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-12 h-12 text-pink-500 animate-spin" />
          <p className="text-slate-600 font-medium">Loading analytics...</p>
        </div>
      </div>
    );
  }

  if (!hasDirectReports) return null;

  return (
    <div className="pt-24 min-h-screen bg-slate-50">
      <main className="mx-auto px-10 lg:px-20 xl:px-32 py-8">
        {/* Header */}
        <div className="bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500 rounded-2xl p-8 mb-8 text-white shadow-xl">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <div className="flex items-center gap-4 mb-2">
                <BarChart3 className="w-10 h-10" />
                <h1 className="text-3xl font-bold">Team Analytics</h1>
              </div>
              <p className="text-white/80 ml-14">
                Operational insights for your team &mdash; {selectedYear}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-white/70" />
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(Number(e.target.value))}
                className="px-4 py-2 border border-white/30 rounded-lg bg-white/20 text-white font-medium focus:outline-none focus:ring-2 focus:ring-white/50 backdrop-blur-sm"
              >
                {yearOptions.map((y) => (
                  <option key={y} value={y} className="text-slate-700">
                    {y}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Row 1 — Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <SummaryCard
            icon={<FileText className="w-6 h-6 text-indigo-500" />}
            iconBg="bg-indigo-100"
            value={statusCounts.total}
            label="Total Connects"
            sub={`In ${selectedYear}`}
          />
          <SummaryCard
            icon={<CheckCircle className="w-6 h-6 text-green-500" />}
            iconBg="bg-green-100"
            value={statusCounts.completed}
            label="Completed"
            sub="Reviews done"
          />
          <SummaryCard
            icon={<Clock className="w-6 h-6 text-blue-500" />}
            iconBg="bg-blue-100"
            value={statusCounts.inProgress}
            label="Pending Review"
            sub="Awaiting action"
          />
          <SummaryCard
            icon={<Target className="w-6 h-6 text-pink-500" />}
            iconBg="bg-pink-100"
            value={`${completionRate.toFixed(0)}%`}
            label="Completion Rate"
            sub={`${statusCounts.completed} of ${statusCounts.total}`}
          />
        </div>

        {/* Row 2 — Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Donut — Connect Status */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
            <h3 className="text-lg font-semibold text-slate-800 mb-6 flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-pink-500" />
              Connect Status
            </h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={donutData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={95}
                    paddingAngle={3}
                    dataKey="value"
                    strokeWidth={0}
                  >
                    {donutData.map((entry, i) => (
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      borderRadius: '12px',
                      border: '1px solid #e2e8f0',
                      fontSize: '13px',
                    }}
                  />
                  <Legend
                    verticalAlign="bottom"
                    iconType="circle"
                    iconSize={10}
                    formatter={(value: string) => (
                      <span className="text-sm text-slate-600">{value}</span>
                    )}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Bar — Connects per Quarter */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
            <h3 className="text-lg font-semibold text-slate-800 mb-6 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-pink-500" />
              Connects per Quarter
            </h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={quarterData} barCategoryGap="20%">
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis
                    dataKey="quarter"
                    tick={{ fontSize: 13, fill: '#64748b' }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    allowDecimals={false}
                    tick={{ fontSize: 13, fill: '#64748b' }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip
                    contentStyle={{
                      borderRadius: '12px',
                      border: '1px solid #e2e8f0',
                      fontSize: '13px',
                    }}
                  />
                  <Legend iconType="circle" iconSize={10} />
                  <Bar dataKey="Completed" fill="#22c55e" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="In Progress" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="Revision Requested" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Row 3 — Core Priorities + PIP */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Core Priorities Coverage */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
            <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
              <Target className="w-5 h-5 text-pink-500" />
              Core Priorities Coverage
            </h3>
            <div className="flex items-end gap-4 mb-4">
              <span className="text-4xl font-bold text-slate-900">
                {cpCoverage.set}
                <span className="text-lg font-medium text-slate-400">/{cpCoverage.total}</span>
              </span>
              <span className="text-sm text-slate-500 mb-1">employees have priorities set</span>
            </div>
            <div className="w-full h-3 bg-slate-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-pink-500 to-purple-500 rounded-full transition-all duration-500"
                style={{ width: `${cpCoverage.pct}%` }}
              />
            </div>
            <div className="mt-4 space-y-2">
              {directReports.map((dr) => (
                <div
                  key={dr.employeeId}
                  className="flex items-center justify-between py-1.5 px-3 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full bg-gradient-to-br from-pink-500 to-purple-500 flex items-center justify-center text-white text-xs font-semibold">
                      {dr.displayName.charAt(0)}
                    </div>
                    <span className="text-sm text-slate-700 font-medium">{dr.displayName}</span>
                  </div>
                  {corePriorityMap[dr.employeeId] ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-green-50 text-green-600 border border-green-200">
                      Set
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-50 text-amber-600 border border-amber-200">
                      Not Set
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* PIP Overview */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
            <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
              <ShieldAlert className="w-5 h-5 text-pink-500" />
              PIP Overview
            </h3>
            <div className="flex items-end gap-4 mb-6">
              <span className="text-4xl font-bold text-slate-900">{pipOverview.count}</span>
              <span className="text-sm text-slate-500 mb-1">
                active PIP{pipOverview.count !== 1 ? 's' : ''} on team
              </span>
            </div>

            {pipOverview.count === 0 ? (
              <div className="text-center py-8">
                <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-3" />
                <p className="text-slate-500 font-medium">No active PIPs</p>
                <p className="text-slate-400 text-sm mt-1">Your entire team is in good standing</p>
              </div>
            ) : (
              <div className="space-y-3">
                {pipOverview.employees.map((emp, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between p-3 bg-orange-50 rounded-xl border border-orange-200"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-orange-200 flex items-center justify-center">
                        <AlertTriangle className="w-4 h-4 text-orange-600" />
                      </div>
                      <span className="font-medium text-slate-800">{emp.name}</span>
                    </div>
                    {emp.endDate && (
                      <span className="text-xs text-orange-700 font-medium">
                        Ends{' '}
                        {formatDateInIST(emp.endDate)}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Row 4 — Per-Employee Summary Table */}
        <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-slate-200 mb-8">
          <div className="p-6 border-b border-slate-100">
            <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
              <Users className="w-5 h-5 text-pink-500" />
              Employee Summary
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-6">
                    Employee
                  </th>
                  <th className="text-center text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-6">
                    Connects
                  </th>
                  <th className="text-center text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-6">
                    Completed
                  </th>
                  <th className="text-center text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-6">
                    Pending
                  </th>
                  <th className="text-center text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-6">
                    Core Priorities
                  </th>
                  <th className="text-center text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-6">
                    PIP
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {employeeSummaries.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center">
                      <Users className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                      <p className="text-slate-500 font-medium">No direct reports found</p>
                    </td>
                  </tr>
                ) : (
                  paginatedEmployeeSummaries.map((emp) => (
                    <tr
                      key={emp.employeeId}
                      className="hover:bg-pink-50/50 transition-colors"
                    >
                      <td className="px-6 py-4 align-middle">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-pink-500 to-purple-500 flex items-center justify-center text-white text-xs font-semibold shadow-sm">
                            {emp.employeeName.charAt(0)}
                          </div>
                          <span className="font-medium text-slate-800">
                            {emp.employeeName}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 align-middle text-center text-sm font-semibold text-slate-700">
                        {emp.totalConnects}
                      </td>
                      <td className="px-6 py-4 align-middle text-center">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-green-50 text-green-600 border border-green-200">
                          {emp.completed}
                        </span>
                      </td>
                      <td className="px-6 py-4 align-middle text-center">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-50 text-blue-600 border border-blue-200">
                          {emp.inProgress + emp.revisionRequested}
                        </span>
                      </td>
                      <td className="px-6 py-4 align-middle text-center">
                        {emp.corePrioritySet ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-green-50 text-green-600 border border-green-200">
                            Set
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-50 text-amber-600 border border-amber-200">
                            Not Set
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 align-middle text-center">
                        {emp.isOnPip ? (
                          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-red-50 text-red-600 border border-red-200">
                            <AlertTriangle className="w-3 h-3" />
                            Active
                          </span>
                        ) : (
                          <span className="text-sm text-slate-400">&mdash;</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <Pagination
            currentPage={empSummaryPage}
            totalPages={empSummaryTotalPages}
            onPageChange={setEmpSummaryPage}
            totalItems={employeeSummaries.length}
            itemsPerPage={ITEMS_PER_PAGE}
          />
        </div>
      </main>

      {/* Back to top */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 p-3 bg-gradient-to-r from-pink-500 to-purple-600 text-white rounded-full shadow-lg shadow-pink-500/25 hover:shadow-xl hover:shadow-pink-500/30 transition-all z-50"
        >
          <ArrowUp className="w-5 h-5" />
        </button>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Summary Card                                                       */
/* ------------------------------------------------------------------ */

function SummaryCard({
  icon,
  iconBg,
  value,
  label,
  sub,
}: {
  icon: React.ReactNode;
  iconBg: string;
  value: number | string;
  label: string;
  sub: string;
}) {
  return (
    <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
      <div className="flex items-center justify-between mb-4">
        <div className={`p-3 rounded-xl ${iconBg}`}>{icon}</div>
        <span className="text-3xl font-bold text-slate-900">{value}</span>
      </div>
      <h3 className="text-slate-600 font-medium">{label}</h3>
      <p className="text-slate-400 text-sm mt-1">{sub}</p>
    </div>
  );
}
